import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";
import {
  ShieldCheck, GitBranch, Sparkles, Globe2, ArrowRight, CircleCheck,
  FlaskConical, Boxes, Cpu, Search, BadgeCheck, FileText,
} from "lucide-react";

const CATEGORIES = [
  "Founding Engineers", "AI Researchers", "ML Systems Engineers", "Distributed Systems",
  "Research Scientists", "Quantitative Developers", "LLM Infrastructure", "Computer Vision",
  "Robotics Engineers", "Applied AI", "Platform Engineers", "Technical Cofounders",
  "Data Scientists", "Backend Engineers", "Security Engineers",
];

const EXAMPLE_PROMPT =
  "We need a founding backend engineer with strong Python, distributed-systems and PostgreSQL experience. Comfortable working directly with founders, shipping quickly and relocating to New York.";

const FAQS = [
  ["Is this a résumé database?", "No. The résumé is roughly 20% of evaluation. Every candidate is assessed through projects, technical evidence, assessments, interviews, references and startup suitability."],
  ["How are candidates vetted?", "Candidates progress through a structured pipeline: completeness review, identity and experience verification, technical assessment, live interviews, reference checks and final review before they are ever published."],
  ["What does the AI actually do?", "Team Assembly interprets your natural-language request into editable criteria, retrieves evidence-backed candidates and explains every recommendation. AI supports discovery — it never makes the hiring decision."],
  ["Do you support international relocation?", "Yes. Our Global Talent Mobility Program coordinates international cases with qualified immigration counsel and authorized sponsors. Eligibility and approval remain subject to sponsor, consular and government review."],
];

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function Home() {
  const [prompt, setPrompt] = useState(EXAMPLE_PROMPT);
  const [examples, setExamples] = useState([]);
  const [stats, setStats] = useState(null);
  const [faqOpen, setFaqOpen] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/public/example-candidates").then((r) => setExamples(r.data.candidates)).catch(() => {});
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const goSearch = () => {
    localStorage.setItem("ta_prefill_prompt", prompt);
    nav("/register?role=startup_employer");
  };

  return (
    <div className="relative">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-44 lg:pb-28">
        <div className="absolute inset-0 ta-grid-radial opacity-60" aria-hidden />
        <div className="absolute -top-40 right-0 h-[500px] w-[500px] rounded-full bg-azure/10 blur-[120px]" aria-hidden />
        <div className="mx-auto max-w-7xl px-5 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 mb-6">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald animate-pulse-line" />
                  <span className="label-mono text-muted">Curated global technical talent network</span>
                </div>
                <h1 className="font-display font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ink">
                  Assemble the technical team<br />
                  <span className="text-muted">your startup was meant to have.</span>
                </h1>
                <p className="mt-6 text-base lg:text-lg text-muted max-w-xl leading-relaxed">
                  Describe the engineer, researcher or technical leader you need. Team Assembly searches a
                  deeply vetted global talent network and identifies the people most likely to succeed inside
                  your startup — <span className="text-ink">discovered through evidence, not résumé keywords.</span>
                </p>
              </motion.div>

              {/* NL prompt box */}
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
                className="mt-8 rounded-2xl border border-white/10 bg-surface p-2 ta-accent-glow">
                <div className="rounded-xl bg-base/60 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Search size={14} className="text-azure" />
                    <span className="label-mono text-faint">Describe the role — natural language</span>
                  </div>
                  <textarea
                    data-testid="hero-prompt-input"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={3}
                    className="w-full resize-none bg-transparent text-sm text-ink placeholder:text-faint focus:outline-none leading-relaxed"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-white/10">
                    <span className="text-xs text-faint">AI interprets your request into editable criteria.</span>
                    <button data-testid="hero-search-btn" onClick={goSearch}
                      className="group inline-flex items-center gap-2 rounded-full bg-azure px-5 py-2.5 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">
                      Find Exceptional Talent
                      <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform duration-200" />
                    </button>
                  </div>
                </div>
              </motion.div>

              <div className="mt-6 flex flex-wrap gap-6 text-sm">
                <button onClick={() => nav("/register?role=candidate")} data-testid="hero-apply-btn"
                  className="text-muted hover:text-ink transition-colors duration-200 underline underline-offset-4 decoration-white/20">
                  Apply to join as talent
                </button>
                <button onClick={() => nav("/register?role=startup_employer")}
                  className="text-muted hover:text-ink transition-colors duration-200 underline underline-offset-4 decoration-white/20">
                  Request a demo
                </button>
              </div>
            </div>

            {/* Example candidate cards */}
            <div className="lg:col-span-5 space-y-3">
              {examples.slice(0, 3).map((c, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + i * 0.12 }}
                  className="rounded-xl border border-white/10 bg-surface p-4 hover:-translate-y-1 hover:border-azure/40 transition-[transform,border-color] duration-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-display font-bold text-ink">{i === 0 ? ((c.display_name || "").replace(/^\S+/, "Nathalie") || "Nathalie") : c.display_name}</p>
                      <p className="text-xs text-muted mt-0.5">{c.headline}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald/10 px-2 py-1 text-[10px] text-emerald">
                      <BadgeCheck size={11} /> {(c.verification_level || "").replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(c.top_skills || []).map((s) => (
                      <span key={s} className="rounded bg-white/5 px-2 py-0.5 text-[10px] font-mono text-muted">{s}</span>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center justify-between text-[11px] text-faint font-mono">
                    <span>{c.country}</span>
                    <span>{c.years_experience} yrs exp</span>
                  </div>
                </motion.div>
              ))}
              <p className="text-center text-[10px] text-faint font-mono">Fictional demonstration profiles</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative z-10 border-y border-white/10 bg-surface/50">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            ["Résumé weighting", "~20%"],
            ["Evidence signals evaluated", "16+"],
            ["Talent categories", `${stats?.categories || 30}+`],
            ["Countries represented", `${stats?.countries || 40}+`],
          ].map(([label, val]) => (
            <div key={label}>
              <p className="font-display text-3xl font-bold text-ink">{val}</p>
              <p className="label-mono text-faint mt-1">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="relative z-10 py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="label-mono text-azure mb-3">How Team Assembly works</p>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl max-w-2xl">
              Most of the sourcing, screening and evaluation is already done before your first conversation.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {[
              [Search, "Describe the role", "Write a natural-language request. AI interprets it into structured, editable criteria."],
              [GitBranch, "Search the evidence", "We match against projects, achievements, assessments and verified experience — not keywords."],
              [Sparkles, "Understand every match", "Each recommendation is explained with the specific evidence behind it and any gaps."],
              [BadgeCheck, "Engage with confidence", "Open profiles, compare candidates, message and request interviews — all in one workspace."],
            ].map(([Icon, title, body], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="bg-surface p-7 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="font-mono text-xs text-faint">0{i + 1}</span>
                    <Icon size={18} className="text-azure" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-ink">{title}</h3>
                  <p className="mt-2 text-sm text-muted leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BEYOND THE RESUME */}
      <section id="evidence" className="relative z-10 py-24 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="label-mono text-azure mb-3">Beyond the résumé</p>
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
                Your résumé is only the beginning.
              </h2>
              <p className="mt-5 text-muted leading-relaxed">
                Team Assembly evaluates what you have built, how you think, how you collaborate and the
                environments in which you perform best. Every important claim is traced to a source and a
                verification level.
              </p>
              <div className="mt-8 space-y-3">
                {["Candidate declared", "Supported by evidence", "Assessed", "Reference verified", "Document verified", "Strongly verified"].map((v, i) => (
                  <div key={v} className="flex items-center gap-3">
                    <div className="h-1 flex-1 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-azure to-emerald" style={{ width: `${40 + i * 10}%` }} />
                    </div>
                    <span className="label-mono text-faint w-40 text-right">{v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-surface p-6">
                <div className="flex items-center justify-between mb-5">
                  <span className="label-mono text-faint">Evidence graph</span>
                  <span className="font-mono text-[10px] text-emerald">verified · traceable</span>
                </div>
                <div className="font-mono text-xs text-muted space-y-2 leading-relaxed">
                  <p><span className="text-azure">Distributed systems</span> → payment-event project</p>
                  <p className="pl-6">→ <span className="text-ink">40M monthly events</span> processed</p>
                  <p className="pl-12">→ architecture assessment <span className="text-emerald">✓ evaluator verified</span></p>
                </div>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[[Cpu, "Skills", "normalized taxonomy"], [Boxes, "Projects", "contribution & impact"], [FlaskConical, "Assessments", "scored & versioned"], [ShieldCheck, "Verification", "identity & references"], [FileText, "Achievements", "quantified outcomes"], [Globe2, "Mobility", "relocation feasibility"]].map(([Icon, t, s]) => (
                    <div key={t} className="rounded-lg border border-white/10 p-3">
                      <Icon size={16} className="text-azure mb-2" />
                      <p className="text-sm text-ink font-medium">{t}</p>
                      <p className="text-[11px] text-faint">{s}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CATEGORIES MARQUEE */}
      <section className="relative z-10 py-14 border-t border-white/10 overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 mb-6">
          <p className="label-mono text-faint">Talent categories</p>
        </div>
        <div className="flex gap-3 flex-wrap px-5 lg:px-8 max-w-7xl mx-auto">
          {CATEGORIES.map((c) => (
            <span key={c} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-muted hover:border-azure/40 hover:text-ink transition-colors duration-200">{c}</span>
          ))}
        </div>
      </section>

      {/* MOBILITY CTA */}
      <section className="relative z-10 py-24 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-surface to-base p-10 lg:p-14 relative overflow-hidden">
              <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-azure/10 blur-[100px]" aria-hidden />
              <div className="relative max-w-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <Globe2 size={18} className="text-azure" />
                  <span className="label-mono text-azure">Global Talent Mobility Program</span>
                </div>
                <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">
                  Bring exceptional international talent to your team.
                </h2>
                <p className="mt-5 text-muted leading-relaxed">
                  From $25,000. We coordinate international-mobility cases with qualified immigration
                  professionals and authorized program sponsors. Eligibility and approval remain subject to
                  sponsor, consular and government review.
                </p>
                <button onClick={() => nav("/mobility")} data-testid="home-mobility-btn"
                  className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm text-ink hover:bg-white hover:text-black transition-colors duration-200">
                  Explore mobility support <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 py-24 border-t border-white/10">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <p className="label-mono text-azure mb-3">Frequently asked</p>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-10">Questions, answered.</h2>
          </Reveal>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {FAQS.map(([q, a], i) => (
              <div key={q} className="py-5">
                <button onClick={() => setFaqOpen(faqOpen === i ? -1 : i)} data-testid={`faq-${i}`}
                  className="flex w-full items-center justify-between text-left">
                  <span className="font-display font-bold text-ink">{q}</span>
                  <span className={`text-azure transition-transform duration-200 ${faqOpen === i ? "rotate-45" : ""}`}>+</span>
                </button>
                {faqOpen === i && <p className="mt-3 text-sm text-muted leading-relaxed">{a}</p>}
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-2 text-xs text-faint">
            <CircleCheck size={14} className="text-emerald" />
            Team Assembly uses AI to support talent discovery. Recommendations are decision-support tools and do not replace human review.
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
